import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';

import {
  customFieldDefinitionSchema,
  documentTypeSchema,
  normClauseSchema,
  processAreaSchema,
  type CustomFieldDefinition,
  type DocumentType,
  type NormClauseRow,
  type ProcessArea,
} from './types';

// Datos de Configuración › Catálogos. La clave de caché comparte el prefijo de los
// selectores (`lib/quality-catalogs`): un alta o una desactivación invalida ese
// prefijo y refresca también los filtros de la Lista Maestra y el alta de documento.

export interface CatalogRowsByPath {
  'document-types': DocumentType;
  'process-areas': ProcessArea;
  'norm-catalog': NormClauseRow;
  'custom-field-definitions': CustomFieldDefinition;
}
export type CatalogPath = keyof CatalogRowsByPath;

const ROW_SCHEMAS: { [P in CatalogPath]: z.ZodType<CatalogRowsByPath[P]> } = {
  'document-types': documentTypeSchema,
  'process-areas': processAreaSchema,
  'norm-catalog': normClauseSchema,
  'custom-field-definitions': customFieldDefinitionSchema,
};

const Q = '/api/v1/quality';

const envelope = z.object({ success: z.literal(true), data: z.unknown() });

function rowSchema<P extends CatalogPath>(path: P): z.ZodType<CatalogRowsByPath[P]> {
  return ROW_SCHEMAS[path];
}

const catalogPrefix = (companyId: string | null, path: CatalogPath): unknown[] => [
  'quality-catalogs',
  companyId,
  path,
];

/** Filas completas de un catálogo de la empresa activa (activas y desactivadas). */
export function useCatalogRows<P extends CatalogPath>(
  path: P,
  { enabled }: { enabled: boolean },
): UseQueryResult<CatalogRowsByPath[P][]> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  const rows = z.array(rowSchema(path));
  return useQuery({
    queryKey: [...catalogPrefix(activeCompanyId, path), 'full'],
    queryFn: async () => rows.parse(envelope.parse(await api.get<unknown>(`${Q}/${path}`)).data),
    enabled: enabled && activeCompanyId !== null,
  });
}

export interface SaveCatalogInput {
  /** null = alta; id = cambio de ese elemento. */
  id: string | null;
  body: Record<string, unknown>;
}

/** Alta (POST) o cambio (PATCH) de un elemento del catálogo. Devuelve la fila
 *  validada; el rechazo del API se propaga para mostrarlo en el formulario. */
export function useSaveCatalogItem<P extends CatalogPath>(
  path: P,
): UseMutationResult<CatalogRowsByPath[P], Error, SaveCatalogInput> {
  const api = useApi();
  const qc = useQueryClient();
  const { activeCompanyId } = useActiveCompany();
  const row = rowSchema(path);
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const raw =
        id === null
          ? await api.post<unknown>(`${Q}/${path}`, body)
          : await api.patch<unknown>(`${Q}/${path}/${id}`, body);
      return row.parse(envelope.parse(raw).data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogPrefix(activeCompanyId, path) }),
  });
}
